import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  PageTitle,
  FlexContainer,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  TextField,
  Divider,
  Avatar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { useBusinessMember } from '../../redux/slices/businessMember/useBusinessMember';
import { useBusiness } from '../../redux/slices/business/useBusiness';
import { updateBusiness, updateBusinessDomain } from '../../redux/slices/business/businessSlice';
import { useExpert } from '../../redux/slices/expert/useExpert';
import * as triplinkService from '../../utils/triplinkService'
import { useAccessToken } from '../../redux/slices/accessToken/useAccessToken';

interface BusinessInfo {
  name: string;
  description: string;
  logo: string;
  socialLinks: {
    instagram: string;
    facebook: string;
    linkedin: string;
  };
  customDomain: string;
  enableCustomDomain: boolean;
  brandingEnabled: boolean;
  primaryColor: string;
  location: string;
  website: string;
  phone: string;
  email: string;
}

type SocialPlatform = keyof BusinessInfo["socialLinks"];

const MAX_IMAGE_SIZE_MB = 2
const MAX_IMAGE_SIZE_BYTE = MAX_IMAGE_SIZE_MB * 1024 * 1024

const BusinessPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { body: businessMembers } = useBusinessMember()
  const { body: expert } = useExpert()
  const { body: accessToken } = useAccessToken()
  const isBusinessOwner = businessMembers?.find(bm => bm.expert_id == expert?.id)?.is_owner;

  const { body } = useBusiness()

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDomainEditing, setIsDomainEditing] = useState<boolean>(false);
  const [business, setBusiness] = useState<BusinessInfo>({
    name: body?.name ?? '',
    description: body?.description ?? '',
    logo: body?.logo_url ?? '',
    socialLinks: {
      instagram: body?.instagram_url ?? '',
      facebook: body?.facebook_url ?? '',
      linkedin: body?.linkedin_url ?? '',
    },
    customDomain: body?.domain ?? '',
    enableCustomDomain: false,
    brandingEnabled: true,
    primaryColor: '#00A699',
    location: body?.location ?? '',
    website: body?.website ?? '',
    phone: body?.phone ?? '',
    email: body?.email ?? ''
  });

  const [editedBusiness, setEditedBusiness] = useState<BusinessInfo>(business);
  const [showCopySuccess, setShowCopySuccess] = useState<string | null>(null);
  const [domainError, setDomainError] = useState(false);

  const [isDomainChangeAllowed, setIsDomainChangeAllowed] = useState(false);
  const [isDomainAvailable, setIsDomainAvailable] = useState(true)
  const [checkingDomainName, setCheckingDomainName] = useState(false);

  // Check if the domain name has ever been changed within 30 days
  useEffect(() => {
    if (!accessToken) return
    const checkDomainChangeability = async () => {
      try {
        const result = await triplinkService.isDomainChangeAllowed(accessToken)
        setIsDomainChangeAllowed(result);
      } catch (error) {
        setIsDomainChangeAllowed(false)
      }
    }

    checkDomainChangeability()
  }, [accessToken])

  const domainNameChanged = business.customDomain !== editedBusiness.customDomain;

  useEffect(() => {
    if (!domainNameChanged || !editedBusiness.customDomain) return;

    setCheckingDomainName(true);
    const handler = setTimeout(() => {
      const checkDomainNameAvailability = async (input: string) => {
        try {
          const result = await triplinkService.checkBusinessDomainNameAvailability(input);

          setIsDomainAvailable(result);
        } catch (error) {
          setIsDomainAvailable(false);
        } finally {
          setCheckingDomainName(false);
        }
      };

      checkDomainNameAvailability(editedBusiness.customDomain);
    }, 2000); // 2s debounce

    return () => {
      clearTimeout(handler); // clear previous timeout if input changes quickly
    };
  }, [editedBusiness.customDomain, domainNameChanged]);


  const handleSave = () => {
    const businessUpdates = {
      name: editedBusiness.name,
      description: editedBusiness.description,
      website: editedBusiness.website,
      instagram_url: editedBusiness.socialLinks.instagram,
      facebook_url: editedBusiness.socialLinks.facebook,
      linkedin_url: editedBusiness.socialLinks.linkedin,
    }

    // Check if the domain has the valid pattern
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/
    if (editedBusiness.website.length > 0 && !domainRegex.test(editedBusiness.website)) {
      setDomainError(true)
      return
    }

    dispatch(updateBusiness({
      data: businessUpdates,
    }))

    setBusiness(editedBusiness);
    setIsEditing(false);
  };

  const handleDomainSave = () => {
    const updates = {
      domain: editedBusiness.customDomain
    }

    dispatch(updateBusinessDomain(updates))
  }

  const handleCancel = () => {
    setEditedBusiness(business);
    setIsEditing(false);
    setIsDomainEditing(false);
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file && file.size > MAX_IMAGE_SIZE_BYTE) {
      alert(`Image is too large, Please upload a file smaller than ${MAX_IMAGE_SIZE_MB}MB`)
      return
    }

    if (file && accessToken) {
      try {
        const publicUrl = await triplinkService.uploadBusinessLogo(accessToken, file)

        setEditedBusiness({
          ...editedBusiness,
          logo: publicUrl
        })
      } catch (error) {
        alert('Failed to upload business logo image')
      }
    }
  };

  const handleSocialLinkChange = (platform: SocialPlatform, value: string) => {
    setEditedBusiness({
      ...editedBusiness,
      socialLinks: {
        ...editedBusiness.socialLinks,
        [platform]: value
      }
    });
  };

  const handleCopyLink = async (type: "meeting" | "collection") => {
    const link = business.customDomain;
    try {
      await navigator.clipboard.writeText(link);
      setShowCopySuccess(type);
      setTimeout(() => setShowCopySuccess(null), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const handleSettingChange = (path: string[], value: any) => {
    const newBusiness = { ...business };
    let current: any = newBusiness;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setBusiness(newBusiness);
  };

  const renderEditComponent = () => {
    if (!isBusinessOwner) return

    if (isEditing) {
      return (
        <Box>
          <IconButton onClick={handleSave} color="primary">
            <SaveIcon />
          </IconButton>
          <IconButton onClick={handleCancel} color="error">
            <CancelIcon />
          </IconButton>
        </Box>)
    } else {
      return (
        <IconButton onClick={() => setIsEditing(true)}>
          <EditIcon />
        </IconButton>
      )
    }
  }

  const renderDomainEditComponent = () => {
    if (!isBusinessOwner) return

    if (isDomainEditing) {
      return (
        <Box>
          <IconButton onClick={handleDomainSave} color="primary" disabled={!domainNameChanged || checkingDomainName || !isDomainAvailable || !editedBusiness.customDomain}>
            <SaveIcon />
          </IconButton>
          <IconButton onClick={handleCancel} color="error">
            <CancelIcon />
          </IconButton>
        </Box>)
    } else {
      return (
        <IconButton onClick={() => setIsDomainEditing(true)}>
          <EditIcon />
        </IconButton>
      )
    }
  }

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Business Details</PageTitle>

          <Card>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FlexContainer style={{ justifyContent: 'center', alignItems: "center" }}>
                    <LogoContainer>
                      <Avatar
                        src={`${editedBusiness.logo}?t=${Date.now()}`}
                        style={{
                          width: '100%',
                          height: '100%'
                        }}
                      />
                      {isEditing && (
                        <LogoOverlay>
                          <input
                            accept="image/*"
                            type="file"
                            id="logo-upload"
                            onChange={handleLogoChange}
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="logo-upload">
                            <IconButton component="span">
                              <PhotoCamera />
                            </IconButton>
                          </label>
                        </LogoOverlay>
                      )}
                    </LogoContainer>
                    {isEditing ? (
                      <TextField
                        value={editedBusiness.name}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, name: e.target.value })}
                        variant="standard"
                        sx={{ mt: 2, textAlign: 'center' }}
                      />
                    ) : (
                      <BusinessName>{business.name}</BusinessName>
                    )}
                  </FlexContainer>
                </Grid>

                <Grid item xs={12}>
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">About</Typography>
                      {renderEditComponent()}
                    </Box>
                    {isEditing ? (
                      <TextField
                        value={editedBusiness.description}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, description: e.target.value })}
                        multiline
                        rows={4}
                        fullWidth
                      />
                    ) : (
                      <Typography paragraph>{business.description}</Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Location
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        value={editedBusiness.location}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, location: e.target.value })}
                        variant="outlined"
                        size="small"
                        placeholder="e.g., Sydney, Australia"
                      />
                    ) : (
                      <Typography>{business.location}</Typography>
                    )}
                  </Box>

                  {/* <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={editedBusiness.location}
                        disabled={!isEditing}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, location: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={editedBusiness.phone}
                        disabled={!isEditing}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, phone: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={editedBusiness.email}
                        disabled={!isEditing}
                        onChange={(e) =>
                          setEditedBusiness({
                            ...editedBusiness,
                            email: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid> */}
                </Grid>

                <Grid item md={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Display Links</Typography>
                    {renderEditComponent()}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        value={editedBusiness.website}
                        disabled={!isEditing}
                        onChange={(e) => setEditedBusiness({ ...editedBusiness, website: e.target.value })}
                        error={domainError}
                        helperText={domainError ? 'Please enter a valid domain like example.com' : ''}
                        size="small"
                      />
                    </Grid>
                    {(Object.entries(business.socialLinks) as [SocialPlatform, string][]).map(([platform, handle]) => (
                      <Grid item xs={12} sm={6} key={platform}>
                        <TextField
                          fullWidth
                          label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                          value={isEditing ? editedBusiness.socialLinks[platform] : handle}
                          onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                          disabled={!isEditing}
                          variant="outlined"
                          size="small"
                          placeholder={`Enter your ${platform} handle`}
                          InputProps={{
                            startAdornment: `https://www.${platform}.com/${platform === 'linkedin' ? 'in/' : ''}`
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <SectionTitle>Business Triplink (Custom Domain)</SectionTitle>
                        {isDomainChangeAllowed && renderDomainEditComponent()}
                      </Box>
                      <Box display="flex" flexDirection="column" gap={2} >
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              disabled={!isDomainEditing}
                              value={editedBusiness.customDomain}
                              onChange={(e) => setEditedBusiness({ ...editedBusiness, customDomain: e.target.value })}
                              InputProps={{
                                startAdornment: 'https://triplink.vip/',
                                readOnly: !isDomainChangeAllowed
                              }}
                            />
                            {!isEditing && <IconButton
                              onClick={() => handleCopyLink("meeting")}
                              color={
                                showCopySuccess === "meeting"
                                  ? "success"
                                  : "default"
                              }
                            >
                              <ContentCopyIcon />
                            </IconButton>}
                          </Box>
                          <Box height={15}>
                            {isDomainEditing &&
                              !isDomainAvailable &&
                              domainNameChanged &&
                              <Typography color={"red"} fontStyle={"italic"}>
                                This domain is unavailable. Please try another.
                              </Typography>}
                            {isDomainEditing &&
                              checkingDomainName &&
                              <Typography color={"#F6AD55"} fontStyle={"italic"}>
                                Checking...
                              </Typography>}
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        </ContentContainer>
      </MainContent>
    </PageLayout>
  );
};

interface InfoFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  onChange: (value: string) => void;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value, isEditing, onChange }) => (
  <Box mb={2}>
    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
      {label}
    </Typography>
    {isEditing ? (
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        size="small"
      />
    ) : (
      <Typography>{value}</Typography>
    )}
  </Box>
);

const LogoContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const BusinessLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  .MuiIconButton-root {
    color: white;
  }
`;

const BusinessName = styled.h2`
  font-size: 1.5rem;
  margin: 1rem 0;
  color: #2D3748;
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  color: #2D3748;
  margin-bottom: 1rem;
`;

export default BusinessPage;
